import { AuthService } from './AuthService.js';

export type LogLevel = 'error' | 'warn';
export type LogCategory = 'js' | 'network' | 'asset';

export interface LogEntry {
  level: LogLevel;
  category: LogCategory;
  message: string;
  stack?: string;
  timestamp: string;
}

const MAX_BUFFER = 50;
const FLUSH_INTERVAL_MS = 30_000;

function getApiBase(): string {
  const envUrl = (import.meta as unknown as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.hostname !== 'localhost') return '';
  return 'http://localhost:4000';
}

export class LogCollector {
  private static instance: LogCollector;

  private buffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private errorHandler: ((e: ErrorEvent) => void) | null = null;
  private rejectionHandler: ((e: PromiseRejectionEvent) => void) | null = null;

  private constructor() {
    this.startTimer();
    this.attachWindowListeners();
  }

  static getInstance(): LogCollector {
    if (!LogCollector.instance) {
      LogCollector.instance = new LogCollector();
    }
    return LogCollector.instance;
  }

  static _resetInstance(): void {
    (LogCollector as unknown as { instance: LogCollector | undefined }).instance = undefined;
  }

  private startTimer(): void {
    this.flushTimer = setInterval(() => { this.flush().catch(() => {}); }, FLUSH_INTERVAL_MS);
  }

  private attachWindowListeners(): void {
    if (typeof window === 'undefined') return;

    this.errorHandler = (e: ErrorEvent) => {
      this.captureError('js', e.message ?? 'Unknown error', e.error?.stack);
    };
    this.rejectionHandler = (e: PromiseRejectionEvent) => {
      const msg = e.reason instanceof Error ? e.reason.message : String(e.reason);
      const stack = e.reason instanceof Error ? e.reason.stack : undefined;
      this.captureError('js', `Unhandled rejection: ${msg}`, stack);
    };

    window.addEventListener('error', this.errorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  warn(category: LogCategory, message: string): void {
    this.addEntry({ level: 'warn', category, message, timestamp: new Date().toISOString() });
  }

  captureError(category: LogCategory, message: string, stack: string | undefined): void {
    this.addEntry({ level: 'error', category, message, stack, timestamp: new Date().toISOString() });
  }

  private addEntry(entry: LogEntry): void {
    this.buffer.push(entry);
    if (this.buffer.length >= MAX_BUFFER) {
      this.flush().catch(() => {});
    }
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const entries = this.buffer.splice(0, this.buffer.length);

    try {
      const token = AuthService.getInstance().getToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${getApiBase()}/api/logs/ingest`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ entries }),
      });

      if (!res.ok) {
        // Server rejected — discard silently (don't re-queue to avoid loops)
      }
    } catch {
      // Network failure — discard silently, never crash the game
    }
  }

  getBufferSize(): number { return this.buffer.length; }
  peekBuffer(): ReadonlyArray<LogEntry> { return this.buffer; }

  destroy(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (typeof window !== 'undefined') {
      if (this.errorHandler) window.removeEventListener('error', this.errorHandler);
      if (this.rejectionHandler) window.removeEventListener('unhandledrejection', this.rejectionHandler);
    }
    this.buffer = [];
  }
}
